import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react"
import { Cog, Image as ImageIcon, Loader2, RefreshCw, Save, Upload } from "lucide-react"
import { toast } from "sonner"
import { getSystemConfig, normalizeSystemAssetUrl, uploadSystemLogo, upsertSystemConfig, type SystemConfigDto } from "shared/api/systemConfigService"
import { useSystemBranding } from "shared/branding/SystemBrandingProvider"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Separator } from "shared/ui/separator"

type FormState = {
  systemName: string
  companyName: string
  logoUrl: string
  faviconUrl: string
  systemUrl: string
  supportEmail: string
  supportPhone: string
}

const emptyForm: FormState = {
  systemName: "",
  companyName: "",
  logoUrl: "",
  faviconUrl: "",
  systemUrl: "",
  supportEmail: "",
  supportPhone: "",
}

function toFormState(config: SystemConfigDto): FormState {
  return {
    systemName: config.systemName ?? "",
    companyName: config.companyName ?? "",
    logoUrl: normalizeSystemAssetUrl(config.logoUrl),
    faviconUrl: normalizeSystemAssetUrl(config.faviconUrl),
    systemUrl: config.systemUrl ?? "",
    supportEmail: config.supportEmail ?? "",
    supportPhone: config.supportPhone ?? "",
  }
}

export default function SystemConfiguration() {
  const { applyBranding } = useSystemBranding()
  const [form, setForm] = useState<FormState>(emptyForm)
  const [initialSnapshot, setInitialSnapshot] = useState<FormState>(emptyForm)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement | null>(null)

  const loadConfig = async () => {
    setIsLoading(true)
    try {
      const config = await getSystemConfig()
      const nextForm = toFormState(config)
      setForm(nextForm)
      setInitialSnapshot(nextForm)
    } catch (error) {
      console.error(error)
      toast.error("โหลด System Configuration ไม่สำเร็จ")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadConfig()
  }, [])

  const hasChanges = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialSnapshot),
    [form, initialSnapshot]
  )

  const handleFieldChange = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleReset = () => {
    setForm(initialSnapshot)
  }

  const handleSave = async () => {
    if (!form.systemName.trim()) {
      toast.error("กรุณากรอก System Name")
      return
    }

    setIsSaving(true)
    try {
      const saved = await upsertSystemConfig({
        systemName: form.systemName.trim(),
        companyName: form.companyName.trim() || null,
        logoUrl: form.logoUrl.trim() || null,
        faviconUrl: form.faviconUrl.trim() || null,
        systemUrl: form.systemUrl.trim() || null,
        supportEmail: form.supportEmail.trim() || null,
        supportPhone: form.supportPhone.trim() || null,
      })

      const nextForm = toFormState(saved)
      setForm(nextForm)
      setInitialSnapshot(nextForm)
      applyBranding(saved)
      toast.success("บันทึก System Configuration สำเร็จ")
    } catch (error: any) {
      console.error(error)
      const message = typeof error?.response?.data === "string"
        ? error.response.data
        : "บันทึก System Configuration ไม่สำเร็จ"
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleUploadLogo = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    setIsUploadingLogo(true)
    try {
      const uploaded = await uploadSystemLogo(file)
      setForm((prev) => ({
        ...prev,
        logoUrl: uploaded.assetUrl,
      }))
      toast.success("อัปโหลดโลโก้สำเร็จ")
    } catch (error: any) {
      console.error(error)
      const message = typeof error?.response?.data === "string"
        ? error.response.data
        : "อัปโหลดโลโก้ไม่สำเร็จ"
      toast.error(message)
    } finally {
      setIsUploadingLogo(false)
      event.target.value = ""
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Cog className="h-6 w-6 text-slate-600" />
            System Configuration
          </CardTitle>
          <p className="mt-1 text-sm text-gray-500">
            กำหนดชื่อระบบ โลโก้ favicon และข้อมูลพื้นฐานของแพลตฟอร์ม
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={isLoading || isSaving || !hasChanges}>
            ย้อนกลับ
          </Button>
          <Button variant="outline" onClick={() => void loadConfig()} disabled={isLoading || isSaving}>
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            รีเฟรช
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isSaving}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            บันทึกการตั้งค่า
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Cog className="h-5 w-5 text-indigo-500" />
              System Settings
            </CardTitle>
            <p className="text-sm text-gray-500">
              กำหนดค่าทั่วไปของระบบและข้อมูลบริษัท
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="system-name">System Name</Label>
                <Input
                  id="system-name"
                  value={form.systemName}
                  onChange={(event) => handleFieldChange("systemName", event.target.value)}
                  placeholder="E-PTW Platform"
                  disabled={isLoading || isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name</Label>
                <Input
                  id="company-name"
                  value={form.companyName}
                  onChange={(event) => handleFieldChange("companyName", event.target.value)}
                  placeholder="PTT Global Chemical"
                  disabled={isLoading || isSaving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo-url">Logo URL</Label>
              <div className="flex flex-col gap-2 md:flex-row">
                <Input
                  id="logo-url"
                  value={form.logoUrl}
                  onChange={(event) => handleFieldChange("logoUrl", event.target.value)}
                  placeholder="https://example.com/logo.png"
                  disabled={isLoading || isSaving || isUploadingLogo}
                />
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*,.svg,.ico"
                  className="hidden"
                  onChange={(event) => void handleUploadLogo(event)}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="md:w-auto"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isLoading || isSaving || isUploadingLogo}
                >
                  {isUploadingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Upload File
                </Button>
              </div>
              <p className="text-xs text-gray-500">URL ของโลโก้ที่จะแสดงบนหน้า Login และ Header</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="favicon-url">Favicon URL</Label>
              <Input
                id="favicon-url"
                value={form.faviconUrl}
                onChange={(event) => handleFieldChange("faviconUrl", event.target.value)}
                placeholder="https://example.com/favicon.ico"
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="system-url">System URL</Label>
              <Input
                id="system-url"
                value={form.systemUrl}
                onChange={(event) => handleFieldChange("systemUrl", event.target.value)}
                placeholder="https://epermit.irpc.co.th/"
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="support-email">Support Email</Label>
                <Input
                  id="support-email"
                  value={form.supportEmail}
                  onChange={(event) => handleFieldChange("supportEmail", event.target.value)}
                  placeholder="support@company.com"
                  disabled={isLoading || isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-phone">Support Phone</Label>
                <Input
                  id="support-phone"
                  value={form.supportPhone}
                  onChange={(event) => handleFieldChange("supportPhone", event.target.value)}
                  placeholder="+66-2-123-4567"
                  disabled={isLoading || isSaving}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Logo Preview</Label>
              <div className="flex min-h-[120px] items-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-5">
                {form.logoUrl.trim() ? (
                  <img
                    src={form.logoUrl.trim()}
                    alt={form.systemName || "System logo"}
                    className="max-h-20 max-w-full object-contain"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-md bg-slate-200 text-[11px] text-slate-600">
                    No Image
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-dashed border-slate-200 bg-slate-50/60 shadow-none">
          <CardContent className="flex items-start gap-3 p-4 text-sm text-slate-600">
            <ImageIcon className="mt-0.5 h-5 w-5 text-slate-500" />
            <div>
              ค่าชุดนี้จะถูกใช้กับหน้าจอ Login, Header และ Sidebar ของระบบทันทีหลังบันทึก
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
